import {ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {AdminUser, AdminUserCreateRequest, AdminUserUpdateRequest} from '../models/api.models';
import {AdminPermission, AdminPermissions} from '../models/admin-permissions';
import {AdminPermissionDefinition, AdminPermissionDefinitions} from '../models/admin-permission-definitions';
import {DataService} from '../services/data.service';
import {PermissionService} from '../services/permission.service';
import {RestService} from '../services/rest.service';
import {TranslationService} from '../services/translation.service';
import {TableSortState} from '../table-sort';
import {DialogService} from '../services/dialog.service';

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UsersComponent implements OnInit, OnDestroy {
    users: AdminUser[] = [];
    filteredUsers: AdminUser[] = [];
    tableSort = new TableSortState();
    searchText = '';
    isLoading = false;
    loadError = '';
    canEditUsers = false;
    canDeleteUsers = false;
    canResetPasswords = false;

    isAddModalOpen = false;
    isCreating = false;
    createError = '';
    generatedPassword = '';
    newUser: AdminUserCreateRequest = this.emptyNewUser();

    editUser?: AdminUser;
    selectedPermissions = new Set<string>();
    isSaving = false;
    editMessage = '';
    editMessageType = '';
    isResettingPassword = false;
    resetGeneratedPassword = '';
    resetPasswordError = '';

    readonly permissionGroups = Array.from(new Set(AdminPermissionDefinitions.map(item => item.group)));
    private sessionSubscription: Subscription;

    constructor(private restService: RestService,
                private dataService: DataService,
                private permissionService: PermissionService,
                private translationService: TranslationService,
                private dialogService: DialogService) {
    }

    ngOnInit(): void {
        this.updateCapabilities();
        this.sessionSubscription = this.dataService.dataChangedSession.subscribe(() => {
            this.updateCapabilities();
            this.loadUsers();
        });
        this.loadUsers();
    }

    ngOnDestroy(): void {
        this.sessionSubscription?.unsubscribe();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        if ((!this.isAddModalOpen && !this.editUser)
            || this.isCreating || this.isSaving || this.isResettingPassword
            || this.dialogService.isOpen) return;

        event.preventDefault();
        if (this.editUser) this.closeEditModal();
        else this.closeAddModal();
    }

    loadUsers(): void {
        if (!this.dataService.getSessionIdentifier() || this.isLoading) return;

        this.isLoading = true;
        this.loadError = '';
        this.restService.getAdminUsers()
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: response => {
                    if (response.StatusCode !== 0) {
                        this.loadError = response.StatusMessage || 'The users could not be loaded.';
                        return;
                    }
                    this.users = response.Users || [];
                    this.filterUsers();
                },
                error: () => this.loadError = 'The users could not be loaded.'
            });
    }

    onSearchChanged(value: string): void {
        this.searchText = value || '';
        this.filterUsers();
    }

    openAddModal(): void {
        if (!this.canEditUsers) return;
        this.newUser = this.emptyNewUser();
        this.generatedPassword = '';
        this.createError = '';
        this.isAddModalOpen = true;
    }

    closeAddModal(): void {
        if (this.isCreating) return;
        this.isAddModalOpen = false;
    }

    createUser(): void {
        if (!this.canEditUsers || this.isCreating || this.generatedPassword) return;

        this.isCreating = true;
        this.createError = '';
        const request: AdminUserCreateRequest = {
            Username: this.newUser.Username.trim(),
            Name: this.newUser.Name.trim(),
            Email: this.newUser.Email.trim()
        };
        this.restService.createAdminUser(request)
            .pipe(finalize(() => this.isCreating = false))
            .subscribe({
                next: response => {
                    if (response.StatusCode !== 0 || !response.User || !response.GeneratedPassword) {
                        this.createError = response.StatusMessage || 'The user could not be created.';
                        return;
                    }
                    this.users = [...this.users, response.User]
                        .sort((left, right) => left.Username.localeCompare(right.Username));
                    this.filterUsers();
                    this.generatedPassword = response.GeneratedPassword;
                },
                error: () => this.createError = 'The user could not be created.'
            });
    }

    async copyGeneratedPassword(): Promise<void> {
        if (!this.generatedPassword) return;
        await navigator.clipboard.writeText(this.generatedPassword);
    }

    openEditModal(user: AdminUser): void {
        this.editUser = {...user, Permissions: [...(user.Permissions || [])]};
        this.selectedPermissions = new Set(user.Permissions || []);
        this.editMessage = '';
        this.editMessageType = '';
        this.resetGeneratedPassword = '';
        this.resetPasswordError = '';
    }

    closeEditModal(): void {
        if (this.isSaving || this.isResettingPassword) return;
        this.editUser = undefined;
        this.selectedPermissions.clear();
        this.resetGeneratedPassword = '';
        this.resetPasswordError = '';
    }

    saveUser(): void {
        if (!this.canEditUsers || !this.editUser || this.isSaving || this.isResettingPassword) return;

        const request: AdminUserUpdateRequest = {
            Username: this.editUser.Username,
            Name: this.editUser.Name.trim(),
            Email: (this.editUser.Email || '').trim(),
            Permissions: Array.from(this.selectedPermissions)
        };
        this.isSaving = true;
        this.editMessage = '';
        this.restService.updateAdminUser(request)
            .pipe(finalize(() => this.isSaving = false))
            .subscribe({
                next: response => {
                    if (response.StatusCode !== 0 || !response.User) {
                        this.setEditMessage(response.StatusMessage || 'The user could not be saved.', 'error');
                        return;
                    }
                    this.replaceUser(response.User);
                    this.editUser = {...response.User, Permissions: [...(response.User.Permissions || [])]};
                    this.selectedPermissions = new Set(response.User.Permissions || []);
                    this.setEditMessage(response.StatusMessage || 'User saved successfully.', 'success');
                },
                error: () => this.setEditMessage('The user could not be saved.', 'error')
            });
    }

    async deleteUser(user: AdminUser, event: Event): Promise<void> {
        event.stopPropagation();
        if (!this.canDeleteUsers || this.isCurrentUser(user)) return;
        const confirmation = `${this.translationService.translate('Delete admin user')} ${user.Username}? `
            + this.translationService.translate('This action cannot be undone.');
        const confirmed = await this.dialogService.confirm({
            headerText: 'Delete user',
            bodyText: confirmation,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            danger: true
        });
        if (!confirmed) return;

        this.restService.deleteAdminUser(user.Username).subscribe({
            next: response => {
                if (response.StatusCode === 0) {
                    this.users = this.users.filter(item => item.Username !== user.Username);
                    this.filterUsers();
                } else {
                    void this.dialogService.alert({
                        headerText: 'Error',
                        bodyText: response.StatusMessage || 'The user could not be deleted.',
                        confirmButtonText: 'Close',
                        danger: true
                    });
                }
            },
            error: () => void this.dialogService.alert({
                headerText: 'Error',
                bodyText: 'The user could not be deleted.',
                confirmButtonText: 'Close',
                danger: true
            })
        });
    }

    async resetUserPassword(): Promise<void> {
        if (!this.canResetPasswords || !this.editUser
            || this.isResettingPassword || this.isSaving || this.resetGeneratedPassword) return;

        const confirmation = `${this.translationService.translate('Reset password for')} ${this.editUser.Username}? `
            + this.translationService.translate('The current password will stop working.');
        const confirmed = await this.dialogService.confirm({
            headerText: 'Reset password',
            bodyText: confirmation,
            confirmButtonText: 'Reset password',
            cancelButtonText: 'Cancel',
            danger: true
        });
        if (!confirmed) return;

        this.isResettingPassword = true;
        this.resetPasswordError = '';
        this.editMessage = '';
        this.restService.resetAdminUserPassword(this.editUser.Username)
            .pipe(finalize(() => this.isResettingPassword = false))
            .subscribe({
                next: response => {
                    if (response.StatusCode !== 0 || !response.GeneratedPassword) {
                        this.resetPasswordError = response.StatusMessage
                            || 'The user password could not be reset.';
                        return;
                    }
                    this.resetGeneratedPassword = response.GeneratedPassword;
                },
                error: () => this.resetPasswordError = 'The user password could not be reset.'
            });
    }

    async copyResetPassword(): Promise<void> {
        if (!this.resetGeneratedPassword) return;
        await navigator.clipboard.writeText(this.resetGeneratedPassword);
    }

    togglePermission(permission: AdminPermission, checked: boolean): void {
        if (!this.canTogglePermission(permission)) return;
        if (checked) this.selectedPermissions.add(permission);
        else this.selectedPermissions.delete(permission);
    }

    canTogglePermission(permission: AdminPermission): boolean {
        return this.canEditUsers && !this.isEditingCurrentUser()
            && this.permissionService.has(permission);
    }

    hasPermission(permission: AdminPermission): boolean {
        return this.selectedPermissions.has(permission);
    }

    getPermissionDefinitions(group: string): readonly AdminPermissionDefinition[] {
        return AdminPermissionDefinitions.filter(item => item.group === group);
    }

    isCurrentUser(user: AdminUser): boolean {
        return user.Username.toLowerCase() === (this.dataService.getUserInfo()?.Username || '').toLowerCase();
    }

    isEditingCurrentUser(): boolean {
        return !!this.editUser && this.isCurrentUser(this.editUser);
    }

    getPermissionCount(user: AdminUser): number {
        return (user.Permissions || []).filter(permission =>
            AdminPermissionDefinitions.some(definition => definition.permission === permission)).length;
    }

    getSelectedPermissionCount(): number {
        return AdminPermissionDefinitions.filter(definition =>
            this.selectedPermissions.has(definition.permission)).length;
    }

    getSortedUsers(): AdminUser[] {
        return this.tableSort.sort(
            this.filteredUsers,
            (user, column) => this.getUserSortValue(user, column)
        );
    }

    hasLoggedIn(user: AdminUser): boolean {
        if (!user.LastLoginDate_iso) return false;
        const lastLogin = new Date(user.LastLoginDate_iso);
        return !Number.isNaN(lastLogin.getTime()) && lastLogin.getUTCFullYear() > 1970;
    }

    private filterUsers(): void {
        const search = this.searchText.trim().toLowerCase();
        this.filteredUsers = search === ''
            ? [...this.users]
            : this.users.filter(user =>
                user.Username.toLowerCase().includes(search)
                || (user.Name || '').toLowerCase().includes(search)
                || (user.Email || '').toLowerCase().includes(search));
    }

    private getUserSortValue(user: AdminUser, column: string) {
        switch (column) {
            case 'username':
                return user.Username;
            case 'name':
                return user.Name;
            case 'email':
                return user.Email;
            case 'permissions':
                return this.getPermissionCount(user);
            case 'lastLogin': {
                const lastLogin = user.LastLoginDate_iso;
                if (!lastLogin || !this.hasLoggedIn(user)) return undefined;
                return new Date(lastLogin).getTime();
            }
            default:
                return '';
        }
    }

    private updateCapabilities(): void {
        this.canEditUsers = this.permissionService.has(AdminPermissions.EditUsers);
        this.canDeleteUsers = this.permissionService.has(AdminPermissions.DeleteUser);
        this.canResetPasswords = this.permissionService.has(AdminPermissions.ResetPassword);
    }

    private replaceUser(user: AdminUser): void {
        this.users = this.users.map(item => item.Username === user.Username ? user : item);
        this.filterUsers();
    }

    private setEditMessage(message: string, type: string): void {
        this.editMessage = message;
        this.editMessageType = type;
    }

    private emptyNewUser(): AdminUserCreateRequest {
        return {Username: '', Name: '', Email: ''};
    }
}
